import {
  getLuxonDate,
} from '@/composables/date';

import {
  sequence_repository,
} from '@/repositories';

import {
  SEQUENCE_RESULT_PER_PAGE_COUNT,
  SEQUENCE_STATUS_CONSTANTS,
} from '@/constants';

const state = () => ({
  is_loading: false,

  all_sequence_statistics: {},
  sequence_list: [],
  settings: [],

  criteria_sort_list: ['-updated_at'],
  criteria_start_date_lower_boundary: undefined,
  criteria_start_date_upper_boundary: undefined,
  criteria_status_id_list: [],
  owner_user_id_list: [],
  criteria_creator_user_id_list: [],
  criteria_id_list: [],
  criteria_name: null,

  limit: SEQUENCE_RESULT_PER_PAGE_COUNT,
  offset: 0,
  page: 1,
  sequence_count: null,
  all_sequence_count: null,
});

const getters = {
  isLoading: (state) => state.is_loading,
  criteriaIdList: (state) => state.criteria_id_list,
  criteriaCreatorUserIdList: (state) => state.criteria_creator_user_id_list,
  criteriaSortList: (state) => state.criteria_sort_list,
  criteriaStartDateLowerBoundary: (state) => state.criteria_start_date_lower_boundary,
  criteriaStartDateUpperBoundary: (state) => state.criteria_start_date_upper_boundary,
  criteriaStatusIdList: (state) => state.criteria_status_id_list,
  ownerUserIdList: (state) => state.owner_user_id_list,
  criteriaName: (state) => state.criteria_name,
  sequenceList: (state) => state.sequence_list,
  settings: (state) => state.settings,
  limit: (state) => state.limit,
  page: (state) => state.page,
  offset: (state) => state.offset,
  sequenceCount: (state) => state.sequence_count,
  allOngoingSequenceCount: (state) => state.all_ongoing_sequence_count,
  allSequenceCount: (state) => state.all_sequence_count,
  allFilteredSequenceList: (state) => state.all_filtered_sequence_list,
  allSequenceStatistics: (state) => state.all_sequence_statistics,
};

const actions = {
  getCriteria({ state, rootState }) {
    const user = rootState.user.token_data;
    const start_date_lower_boundary = getLuxonDate(state.criteria_start_date_lower_boundary);
    const start_date_upper_boundary = getLuxonDate(state.criteria_start_date_upper_boundary);

    return {
      id_list: state.criteria_id_list.length > 0
        ? state.criteria_id_list
        : undefined,
      creator_user_id_list: state.criteria_creator_user_id_list.length > 0
        ? state.criteria_creator_user_id_list
        : undefined,
      organization_id: user.organization_id,
      status_id_list: state.criteria_status_id_list.length > 0
        ? state.criteria_status_id_list
        : undefined,
      owner_user_id_list: state.owner_user_id_list.length > 0
        ? state.owner_user_id_list
        : undefined,
      name: state.criteria_name?? undefined,
      limit: state.limit,
      offset: state.offset,
      sort_list: state.criteria_sort_list,
      start_date_lower_boundary: state.criteria_start_date_lower_boundary === undefined
        ? undefined
        : start_date_lower_boundary?.startOf('day').toISO(),
      start_date_upper_boundary: state.criteria_start_date_upper_boundary === undefined
        ? undefined
        : start_date_upper_boundary?.endOf('day').toISO(),
    };
  },

  /**
   * This function will be call only one time in SequenceV2ResultFilterElement to enrich
   * sequence option list and sequence creator list on filter
   * No need to save the result in the store, it will be used only once.
   * @returns {Promise<Array<Object>>}
   */
  async getAllSequenceList({ rootState }) {
    const result = await sequence_repository.searchSequencesLight({
      organization_id: rootState.user.token_data.organization_id,
      limit: 10000,
      status_id_list: [
        SEQUENCE_STATUS_CONSTANTS.ONGOING.ID,
        SEQUENCE_STATUS_CONSTANTS.PAUSED.ID,
        SEQUENCE_STATUS_CONSTANTS.FINISHED.ID,
      ],
    });

    return result.sequence_list;
  },

  async getSequenceStatistics({ commit }, sequence_id_list) {
    if (sequence_id_list.length === 0) {
      commit('set_all_sequence_statistics', {});

      return;
    }

    const result = await sequence_repository.getSequenceStatistics(sequence_id_list);

    commit('set_all_sequence_statistics', result);
  },

  async processSearch({ commit, dispatch }) {
    const criteria = await dispatch('getCriteria');
    const result = await dispatch('search', {
      ...criteria,
      status_id_list: [
        SEQUENCE_STATUS_CONSTANTS.ONGOING.ID,
        SEQUENCE_STATUS_CONSTANTS.PAUSED.ID,
        SEQUENCE_STATUS_CONSTANTS.FINISHED.ID,
      ],
    });
    const settings = await dispatch('getSettings');

    commit('set_sequence_list', result.sequence_list);
    commit('set_sequence_count', result.sequence_count);
    commit('set_settings', settings);
  },
  async processAllSequenceSearch({ commit, dispatch }) {
    commit('set_is_loading', true);
    const {
      // eslint-disable-next-line no-unused-vars
      limit,
      // eslint-disable-next-line no-unused-vars
      offset,
      ...rest_criteria
    } = await dispatch('getCriteria');

    const { sequence_list } = await dispatch('searchLight', {
      ...rest_criteria,
      limit: 10000,
      status_id_list: [
        SEQUENCE_STATUS_CONSTANTS.ONGOING.ID,
        SEQUENCE_STATUS_CONSTANTS.PAUSED.ID,
        SEQUENCE_STATUS_CONSTANTS.FINISHED.ID,
      ],
    });

    const ongoing_sequence_count = sequence_list.filter((sequence) =>
      sequence.status_id === SEQUENCE_STATUS_CONSTANTS.ONGOING.ID
    ).length;

    commit('set_all_ongoing_sequence_count', ongoing_sequence_count);
    commit('set_all_sequence_count', sequence_list.length);
    commit('set_all_filtered_sequence_list', sequence_list);

    await dispatch('getSequenceStatistics', sequence_list.map((sequence) => sequence.id));

    commit('set_is_loading', false);
  },

  async search(_, criteria) {
    const result = await sequence_repository.searchSequences(criteria);

    return result;
  },

  async searchLight(_, criteria) {
    const result = await sequence_repository.searchSequencesLight(criteria);

    return result;
  },

  async getSettings({ commit, rootState }) {
    const result = await sequence_repository.getSettings({
      organization_id_list: [rootState.user.token_data.organization_id],
    });

    commit('set_settings', result.setting_list);

    return result.setting_list;
  },
};

const mutations = {
  set_criteria_id_list(state, criteria_id_list) {
    state.criteria_id_list = criteria_id_list;
  },
  set_criteria_creator_user_id_list(state, criteria_creator_user_id_list) {
    state.criteria_creator_user_id_list = criteria_creator_user_id_list;
  },
  set_criteria_start_date_lower_boundary(state, criteria_start_date_lower_boundary) {
    state.criteria_start_date_lower_boundary = criteria_start_date_lower_boundary;
  },
  set_criteria_start_date_upper_boundary(state, criteria_start_date_upper_boundary) {
    state.criteria_start_date_upper_boundary = criteria_start_date_upper_boundary;
  },
  set_criteria_status_id_list(state, criteria_status_id_list) {
    state.criteria_status_id_list = criteria_status_id_list;
  },
  set_owner_user_id_list(state, owner_user_id_list) {
    state.owner_user_id_list = owner_user_id_list;
  },
  set_criteria_name(state, criteria_name) {
    state.criteria_name = criteria_name;
  },
  set_limit(state, limit) {
    state.limit = limit;
  },
  set_sequence_list(state, sequence_list) {
    state.sequence_list = sequence_list;
  },
  set_settings(state, settings) {
    state.settings = settings;
  },
  set_offset(state, offset) {
    state.offset = offset;
  },
  set_page(state, page) {
    state.page = page;
  },
  set_sequence_count(state, sequence_count) {
    state.sequence_count = sequence_count;
  },
  set_all_sequence_statistics(state, all_sequence_statistics) {
    state.all_sequence_statistics = all_sequence_statistics;
  },
  set_all_ongoing_sequence_count(state, ongoing_sequence_count) {
    state.all_ongoing_sequence_count = ongoing_sequence_count;
  },
  set_all_sequence_count(state, all_sequence_count) {
    state.all_sequence_count = all_sequence_count;
  },
  set_all_filtered_sequence_list(state, all_filtered_sequence_list) {
    state.all_filtered_sequence_list = all_filtered_sequence_list;
  },
  set_is_loading(state, is_loading) {
    state.is_loading = is_loading;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
